import { Box, Button, Grid, TextField, Typography } from '@material-ui/core'
import React, { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { AvailabilityCheckField } from '../../store/AvailabilityCheck/AvailabilityCheck.actions'
import { ViewType } from '../../store/GeneralState/GeneralState.reducer'
import TestIDs from '../../utils/TestIDs'
import colors from '../../utils/colors'
import Dropdown from '../Dropdown'
import './customAnchor.css'

export interface AvailabilityCheckInputProps {
    noProductLink: string
    autoFocusOnMount: boolean
    cities: string[]
    disabledCity: boolean
    disabledHouseNumber: boolean
    disabledStreet: boolean
    disabledZip: boolean
    allowFreeTextStreet?: boolean
    allowFreeTextHouseNumber?: boolean
    focusedFieldId: AvailabilityCheckField
    houseNumbers: string[]
    selectedCity: string
    selectedHouseNumber: string
    selectedStreet: string
    streets: string[]
    zip: string
    queriesAreFinished?: boolean
    setFocusedField: (payload: AvailabilityCheckField) => void
    setSelectedCity?: (
        payload: React.ChangeEvent<{
            name?: string | undefined
            value: unknown
        }>,
    ) => void
    setSelectedHouseNumber?: (
        payload: React.ChangeEvent<{
            name?: string | undefined
            value: unknown
        }>,
    ) => void
    setSelectedStreet?: (
        payload: React.ChangeEvent<{
            name?: string | undefined
            value: unknown
        }>,
    ) => void
    setZip?: (payload: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void
    haveLink?: boolean
}

const AvailabilityCheckInput = (props: AvailabilityCheckInputProps): JSX.Element => {
    const { t } = useTranslation()
    const regexp = /^[0-9\b]+$/
    const {
        autoFocusOnMount,
        cities,
        disabledCity,
        disabledHouseNumber,
        disabledStreet,
        disabledZip,
        allowFreeTextStreet = false,
        allowFreeTextHouseNumber = false,
        focusedFieldId,
        houseNumbers,
        selectedCity,
        selectedHouseNumber,
        selectedStreet,
        streets,
        zip,
        queriesAreFinished,
        setFocusedField,
        setSelectedCity,
        setSelectedHouseNumber,
        setSelectedStreet,
        setZip,
        //noProductLink,
        haveLink,
    } = props

    const zipRef = useRef<HTMLInputElement>(null)
    const cityRef = useRef<HTMLInputElement>(null)
    const streetRef = useRef<HTMLInputElement>(null)
    const houseNumberRef = useRef<HTMLInputElement>(null)

    useEffect(() => {
        switch (focusedFieldId) {
            case AvailabilityCheckField.ZIP:
                if (zipRef.current !== null) {
                    zipRef.current.focus()
                }
                break
            case AvailabilityCheckField.CITY:
                if (cityRef.current !== null) {
                    try {
                        setFocusedField(focusedFieldId)
                        cityRef.current.focus()
                    } catch (error) {
                        console.debug('no matching element to focus')
                    }
                }
                break
            case AvailabilityCheckField.STREET:
                if (streetRef.current !== null) {
                    try {
                        setFocusedField(focusedFieldId)
                        streetRef.current.focus()
                    } catch (error) {
                        console.debug('no matching element to focus')
                    }
                }
                break
            case AvailabilityCheckField.HOUSE_NUMBER:
                if (houseNumberRef.current !== null) {
                    try {
                        setFocusedField(focusedFieldId)
                        houseNumberRef.current.focus()
                    } catch (error) {
                        console.debug('no matching element to focus')
                    }
                }
                break
        }
        return () => {
            // Cleanup
        }
    }, [focusedFieldId, zip, selectedCity, selectedHouseNumber, selectedStreet])

    const nothingFound = queriesAreFinished === true && cities.length === 0 && zip.length === 5

    return (
        <>
            <Grid container {...TestIDs.GET_AVAILABILITY_CHECK('FORM')} spacing={3}>
                <Grid item xs={12} sm={6} md={4}>
                    <TextField
                        {...TestIDs.GET_AVAILABILITY_CHECK('ZIP_INPUT')}
                        error={nothingFound}
                        disabled={disabledZip}
                        InputProps={{
                            className: nothingFound ? 'TextError' : '',
                            onFocus: (): void => setFocusedField(AvailabilityCheckField.ZIP),
                        }}
                        inputProps={{
                            maxLength: 5,
                        }}
                        inputRef={zipRef}
                        fullWidth={true}
                        label={t('zip')}
                        variant={'outlined'}
                        value={zip}
                        onChange={(e): void => {
                            if (
                                setZip &&
                                ((e.currentTarget.value !== '' && regexp.test(e.currentTarget.value)) ||
                                    e.currentTarget.value === '')
                            ) {
                                setZip(e)
                            }
                        }}
                        autoFocus={autoFocusOnMount}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={8}>
                    {cities.length === 1 ? (
                        <TextField
                            {...TestIDs.GET_AVAILABILITY_CHECK('CITY_INPUT')}
                            disabled={true}
                            fullWidth={true}
                            label={t('city')}
                            variant={'outlined'}
                            value={cities[0]}
                        />
                    ) : (
                        <Dropdown
                            testIdentifier={{
                                input: 'CITY_SELECT',
                                viewType: ViewType.AVAILABILITY_CHECK,
                            }}
                            label={t('city')}
                            disabled={disabledCity}
                            inputRef={cityRef}
                            value={selectedCity}
                            onChange={setSelectedCity}
                            options={cities}
                        />
                    )}
                </Grid>
                <Grid item xs={12} md={8}>
                    {streets.length === 1 ? (
                        <TextField
                            {...TestIDs.GET_AVAILABILITY_CHECK('STREET_INPUT')}
                            disabled={true}
                            fullWidth={true}
                            label={t('street')}
                            variant={'outlined'}
                            value={streets[0]}
                        />
                    ) : allowFreeTextStreet ? (
                        <TextField
                            {...TestIDs.GET_AVAILABILITY_CHECK('STREET_INPUT')}
                            disabled={false}
                            fullWidth={true}
                            label={t('street')}
                            variant={'outlined'}
                            value={selectedStreet}
                            inputRef={streetRef}
                            InputProps={{
                                onFocus: (): void => setFocusedField(AvailabilityCheckField.STREET),
                            }}
                            onChange={(e): void => {
                                if (setSelectedStreet) {
                                    setSelectedStreet(
                                        e as unknown as React.ChangeEvent<{
                                            name?: string | undefined
                                            value: unknown
                                        }>,
                                    )
                                }
                            }}
                        />
                    ) : (
                        <Dropdown
                            testIdentifier={{
                                input: 'STREET_SELECT',
                                viewType: ViewType.AVAILABILITY_CHECK,
                            }}
                            label={t('street')}
                            disabled={disabledStreet}
                            inputRef={streetRef}
                            value={selectedStreet}
                            onChange={setSelectedStreet}
                            options={streets}
                        />
                    )}
                </Grid>
                <Grid item xs={12} md={4}>
                    {houseNumbers.length === 1 ? (
                        <TextField
                            {...TestIDs.GET_AVAILABILITY_CHECK('HOUSE_NUMBER_INPUT')}
                            disabled={true}
                            fullWidth={true}
                            label={t('number')}
                            variant={'outlined'}
                            value={houseNumbers[0]}
                        />
                    ) : allowFreeTextHouseNumber ? (
                        <TextField
                            {...TestIDs.GET_AVAILABILITY_CHECK('HOUSE_NUMBER_INPUT')}
                            disabled={false}
                            fullWidth={true}
                            label={t('number')}
                            variant={'outlined'}
                            value={selectedHouseNumber}
                            inputRef={houseNumberRef}
                            InputProps={{
                                onFocus: (): void => setFocusedField(AvailabilityCheckField.HOUSE_NUMBER),
                            }}
                            onChange={(e): void => {
                                if (setSelectedHouseNumber) {
                                    setSelectedHouseNumber(
                                        e as unknown as React.ChangeEvent<{
                                            name?: string | undefined
                                            value: unknown
                                        }>,
                                    )
                                }
                            }}
                        />
                    ) : (
                        <Dropdown
                            label={t('number')}
                            testIdentifier={{
                                input: 'HOUSE_NUMBER_SELECT',
                                viewType: ViewType.AVAILABILITY_CHECK,
                            }}
                            inputRef={houseNumberRef}
                            disabled={disabledHouseNumber}
                            value={selectedHouseNumber}
                            onChange={setSelectedHouseNumber}
                            options={houseNumbers}
                        />
                    )}
                </Grid>
                {/* {haveLink === true && (
                    <Grid item xs={12}>
                        <Typography
                            variant={'body1'}
                            dangerouslySetInnerHTML={{
                                __html: t('notYetAvailableLink', {
                                    link: noProductLink,
                                }),
                            }}
                        />
                    </Grid>
                )} */}
                {haveLink === true && (
                    <Grid
                        container
                        item
                        xs={12}
                        alignItems={'center'}
                        justifyContent={'flex-end'}
                        style={{
                            borderRadius: '4px',
                            backgroundColor: colors.lightGray,
                            margin: '0 12px 12px 12px',
                            color: '#000',
                        }}
                    >
                        <Grid item xs>
                            <Typography variant={'h5'} style={{ fontWeight: 'bold' }}>
                                {t('notYetAvailableBoxHeader')}
                            </Typography>
                            <Typography>{t('notYetAvailableBoxText')}</Typography>
                        </Grid>
                        <Grid item>
                            <Box
                                display="flex"
                                justifyContent={'center'}
                                alignItems={'center'}
                                flexDirection="row"
                                textAlign="center"
                                borderRadius="4px"
                                style={{ backgroundColor: colors.color2 }}
                            >
                                <Button size="medium" href={props.noProductLink}>
                                    {t('notYetAvailableBoxButton')}
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                )}
            </Grid>
        </>
    )
}

export default AvailabilityCheckInput
